import { 
  PhoneAuthProvider, 
  RecaptchaVerifier,
  signInWithPhoneNumber,
  linkWithCredential
} from 'firebase/auth';
import { auth } from '../firebase';
import { doc, updateDoc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { logPhoneAuthFailure, PHONE_AUTH_RECAPTCHA_USER_MESSAGE } from '../utils/errorLogger';
import { Capacitor } from '@capacitor/core';

// Security Phase 1, Stage A (TECH_DEBT.md "Move phoneVerified write
// server-side"): ask the server to confirm phoneVerified against Firebase
// Auth and write the Firestore flags with the Admin SDK. Fire-and-forget BY
// DESIGN in Stage A — never awaited in the user path, so it adds zero
// latency and can never fail a signup; the client's own writes below remain
// the source of truth until Stage B. Exported for the Stage A contract test.
export function fireConfirmPhoneVerified(context = 'unknown') {
  try {
    const callable = httpsCallable(getFunctions(), 'confirmPhoneVerified');
    return callable({})
      .then((res) => {
        console.log(`✅ confirmPhoneVerified (server, ${context}):`, res?.data);
        return res;
      })
      .catch((err) => {
        // Expected to be rare; harmless in Stage A (client write covers it).
        // The soak gate before Stage B reviews these — see TECH_DEBT.md.
        console.warn(`⚠️ confirmPhoneVerified failed (non-blocking, ${context}):`, err?.code, err?.message);
        return null;
      });
  } catch (err) {
    console.warn(`⚠️ confirmPhoneVerified could not start (non-blocking, ${context}):`, err);
    return Promise.resolve(null);
  }
}

class FirebasePhoneAuthService {
  constructor() {
    this.recaptchaVerifier = null;
  }

  // Initialize reCAPTCHA verifier
  initializeRecaptcha(containerId) {
    if (this.recaptchaVerifier) {
      try {
        this.recaptchaVerifier.clear();
      } catch (e) {
        console.warn('⚠️ Error clearing previous reCAPTCHA:', e);
      }
      this.recaptchaVerifier = null;
    }

    // Ensure container exists
    const container = document.getElementById(containerId);
    if (!container) {
      throw new Error(`reCAPTCHA container "${containerId}" not found in DOM`);
    }
    
    // Clear the container
    container.innerHTML = '';

    try {
      this.recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
        size: 'invisible',
        callback: () => {
          console.log('✅ reCAPTCHA solved');
        },
        'expired-callback': () => {
          console.warn('⚠️ reCAPTCHA expired');
        },
        'error-callback': (error) => {
          console.error('❌ reCAPTCHA error:', error);
        }
      });

      return this.recaptchaVerifier;
    } catch (error) {
      console.error('❌ Error creating reCAPTCHA verifier:', error);
      throw new Error('Failed to initialize reCAPTCHA. Please try again.');
    }
  }

  // Send SMS verification code
  async sendVerificationCode(phoneNumber, containerId = 'recaptcha-container') {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('No authenticated user found');
    }

    // Native (Capacitor) path: the web SDK's RecaptchaVerifier cannot run at the
    // capacitor://localhost origin, so app-verification is delegated to the
    // native Firebase SDK (APNs / reCAPTCHA-fallback) via the plugin. The plugin
    // returns a verificationId that is interchangeable with the web SDK's
    // verifyPhoneNumber result, so verifyCode() below is identical on both
    // platforms (PhoneAuthProvider.credential + linkWithCredential).
    if (Capacitor.isNativePlatform()) {
      return this.sendVerificationCodeNative(phoneNumber);
    }

    try {
      // Initialize reCAPTCHA
      const appVerifier = this.initializeRecaptcha(containerId);

      console.log('🔍 Sending SMS verification code to:', phoneNumber);
      
      // For existing users, we need to use a different approach
      // We'll create a temporary phone auth session
      const phoneAuthProvider = new PhoneAuthProvider(auth);
      const session = await phoneAuthProvider.verifyPhoneNumber(phoneNumber, appVerifier);

      return {
        session,
        phoneNumber,
        success: true
      };
    } catch (error) {
      console.error('❌ Error sending verification code:', error);
      throw this.handlePhoneAuthError(error, 'send_sms');
    }
  }

  // Native SMS send via @capacitor-firebase/authentication. skipNativeAuth keeps
  // the JS Firebase SDK as the source of truth: the plugin only performs phone
  // verification and emits the verificationId; the credential is then built and
  // linked to the existing account with the web SDK (see verifyCode). Loaded
  // dynamically so the plugin never executes on the web build.
  async sendVerificationCodeNative(phoneNumber) {
    const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');

    // Clear any listeners left over from a previous attempt.
    await FirebaseAuthentication.removeAllListeners();

    console.log('🔍 [native] Sending SMS verification code to:', phoneNumber);

    return new Promise((resolve, reject) => {
      let settled = false;
      const finish = (fn, arg) => {
        if (settled) return;
        settled = true;
        FirebaseAuthentication.removeAllListeners();
        fn(arg);
      };

      Promise.all([
        FirebaseAuthentication.addListener('phoneCodeSent', (event) => {
          console.log('✅ [native] phoneCodeSent — verificationId received');
          finish(resolve, {
            session: event.verificationId,
            phoneNumber,
            success: true
          });
        }),
        FirebaseAuthentication.addListener('phoneVerificationFailed', (event) => {
          console.error('❌ [native] phoneVerificationFailed:', event.message);
          const err = new Error(event.message || 'Phone verification failed');
          finish(reject, this.handlePhoneAuthError(err, 'send_sms'));
        })
      ])
        .then(() =>
          FirebaseAuthentication.signInWithPhoneNumber({
            phoneNumber,
            skipNativeAuth: true
          })
        )
        .catch((error) => {
          console.error('❌ [native] Error sending verification code:', error);
          finish(reject, this.handlePhoneAuthError(error, 'send_sms'));
        });
    });
  }

  // Verify SMS code
  async verifyCode(session, verificationCode, phoneNumber = null) {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('No authenticated user found');
    }

    try {
      console.log('🔍 Verifying SMS code...');
      
      // Create credential from session and code
      const credential = PhoneAuthProvider.credential(session, verificationCode);
      
      // Check if phone provider is already linked
      const isPhoneLinked = user.providerData.some(provider => provider.providerId === 'phone');
      
      console.log('🔍 Current providers:', user.providerData.map(p => p.providerId).join(', '));
      console.log('🔍 Phone already linked?', isPhoneLinked);
      
      if (!isPhoneLinked) {
        console.log('🔗 Linking phone credential to account...');
        try {
          // Link the phone credential to the existing user
          await linkWithCredential(user, credential);
          console.log('✅ Phone credential linked successfully');
        } catch (linkError) {
          console.error('❌ Link error:', linkError.code, linkError.message);
          
          throw linkError;
        }
      } else {
        console.log('ℹ️ Phone provider already linked, skipping link step');
      }
      
      // After linking (or if already linked), refresh the user to get the updated phone number from Firebase Auth
      await user.reload();
      
      // Get the fresh user object after reload to ensure we have the latest phone number
      const refreshedUser = auth.currentUser;
      
      // After linking, the phone number should be available in user.phoneNumber
      // Priority: 1) provided phoneNumber, 2) refreshedUser.phoneNumber from Firebase Auth
      let phoneToStore = phoneNumber || refreshedUser?.phoneNumber;
      
      console.log('🔍 Phone number to store:', phoneToStore, 'from user.phoneNumber:', refreshedUser?.phoneNumber);
      
      if (!phoneToStore) {
        console.warn('⚠️ No phone number available after verification. Attempting to get from Auth...');
        // Try one more time with a fresh auth state
        const finalUser = auth.currentUser;
        if (finalUser?.phoneNumber) {
          phoneToStore = finalUser.phoneNumber;
          console.log('✅ Found phone number on retry:', phoneToStore);
        }
      }
      
      // Update Firestore to mark phone as verified and store phone number
      await this.updatePhoneVerificationStatus(true, phoneToStore);

      return {
        success: true,
        message: 'Phone number verified successfully'
      };
    } catch (error) {
      console.error('❌ Error verifying code:', error);
      throw this.handlePhoneAuthError(error, 'verify_code');
    }
  }

  // Update phone verification status in Firestore
  async updatePhoneVerificationStatus(verified, phoneNumber = null) {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('No authenticated user found');
    }

    // Stage A: server-side confirmation runs concurrently (fire-and-forget,
    // see fireConfirmPhoneVerified). Everything below is unchanged and still
    // authoritative until Stage B.
    if (verified) {
      fireConfirmPhoneVerified('updatePhoneVerificationStatus');
    }

    try {
      // Get current user data from Firestore to check emailVerified status
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      const currentUserData = userDoc.exists() ? userDoc.data() : {};
      
      // Check both Firebase Auth and Firestore for email verification status
      const emailVerified = user.emailVerified || currentUserData.emailVerified || false;
      
      console.log('🔍 Phone verification - email status check:', {
        userEmailVerified: user.emailVerified,
        firestoreEmailVerified: currentUserData.emailVerified,
        finalEmailVerified: emailVerified,
        phoneVerified: verified
      });
      
      const updateData = {
        phoneVerified: verified,
        phoneVerifiedAt: serverTimestamp(),
        emailVerified: emailVerified, // Use the most accurate email verification status
        isVerified: emailVerified && verified // Only fully verified if both email and phone are verified
      };
      
      console.log('🔍 Phone verification - updateData:', updateData);
      
      // Always try to store the phone number when verification succeeds
      // Priority: 1) provided phoneNumber, 2) user.phoneNumber from Firebase Auth (should be fresh from verifyCode), 3) existing phoneNumber in Firestore
      if (phoneNumber) {
        updateData.phoneNumber = phoneNumber;
        console.log('✅ Using provided phone number:', phoneNumber);
      } else if (verified) {
        // If verified is true but no phoneNumber was provided, try to get it from Firebase Auth
        // Note: verifyCode already reloads the user, so user.phoneNumber should be fresh
        if (user.phoneNumber) {
          updateData.phoneNumber = user.phoneNumber;
          console.log('✅ Using phone number from Firebase Auth:', user.phoneNumber);
        } else {
          // Last resort: reload user if phone number still not available (shouldn't happen)
          console.warn('⚠️ Phone number not in user object, attempting reload...');
          try {
            await user.reload();
            const freshUser = auth.currentUser;
            if (freshUser?.phoneNumber) {
              updateData.phoneNumber = freshUser.phoneNumber;
              console.log('✅ Using phone number from Firebase Auth after reload:', freshUser.phoneNumber);
            } else if (currentUserData.phoneNumber) {
              // Fallback: keep existing one if Auth doesn't have it
              updateData.phoneNumber = currentUserData.phoneNumber;
              console.warn('⚠️ Using existing phone number from Firestore (Auth phone not found):', currentUserData.phoneNumber);
            } else {
              console.error('❌ No phone number available from Auth or Firestore after verification!');
            }
          } catch (reloadError) {
            console.error('❌ Error reloading user:', reloadError);
            // If reload fails, try to use existing phone number from Firestore
            if (currentUserData.phoneNumber) {
              updateData.phoneNumber = currentUserData.phoneNumber;
              console.warn('⚠️ Using existing phone number from Firestore after reload error:', currentUserData.phoneNumber);
            }
          }
        }
      }

      console.log('🔍 Phone verification - final updateData before Firestore write:', updateData);

      // Use setDoc with merge to create document if it doesn't exist
      await setDoc(doc(db, 'users', user.uid), updateData, { merge: true });

      console.log('✅ Phone verification status updated in Firestore');
      
      // Verify what was actually written
      const verifyDoc = await getDoc(doc(db, 'users', user.uid));
      const verifyData = verifyDoc.exists() ? verifyDoc.data() : {};
      console.log('🔍 Phone verification - Firestore document after write:', {
        isVerified: verifyData.isVerified,
        emailVerified: verifyData.emailVerified,
        phoneVerified: verifyData.phoneVerified
      });
    } catch (error) {
      console.error('❌ Error updating phone verification status:', error);
      throw error;
    }
  }

  // Handle phone authentication errors. Returns an Error whose message is
  // safe to show to the user verbatim (marked with isFriendly so callers and
  // re-entrant catch blocks don't wrap it in more boilerplate).
  handlePhoneAuthError(error, phase = 'unknown') {
    // Already translated by an inner catch — pass through untouched so the
    // specific message isn't replaced with the generic fallback.
    if (error?.isFriendly) return error;

    void logPhoneAuthFailure(error, phase);

    const friendly = (message) => {
      const err = new Error(message);
      err.isFriendly = true;
      err.code = error?.code;
      return err;
    };

    const alreadyInUseMessage =
      'This phone number is already registered to another PedalxChange account. ' +
      'Please use a different phone number, or contact us at contact@pedalxchange.com if you believe this is a mistake.';

    switch (error.code) {
      case 'auth/invalid-verification-code':
        return friendly('That verification code isn\'t correct. Please double-check the 6-digit code from your text message and try again.');
      case 'auth/code-expired':
      case 'auth/session-expired':
        return friendly('Your verification code has expired. Please tap "Send Code" to get a new one.');
      case 'auth/invalid-phone-number':
        return friendly('That phone number doesn\'t look valid. Please enter a 10-digit US mobile number (e.g., 949-555-1234).');
      case 'auth/too-many-requests':
        return friendly('Too many attempts. Please wait a little while and try again.');
      case 'auth/captcha-check-failed':
        return friendly(PHONE_AUTH_RECAPTCHA_USER_MESSAGE);
      case 'auth/quota-exceeded':
        return friendly('Text message verification is temporarily unavailable. Please try again later.');
      case 'auth/operation-not-allowed':
        return friendly('Phone verification is currently unavailable. Please contact us at contact@pedalxchange.com');
      case 'auth/credential-already-in-use':
      case 'auth/account-exists-with-different-credential':
        return friendly(alreadyInUseMessage);
      case 'auth/invalid-app-credential':
        return friendly(PHONE_AUTH_RECAPTCHA_USER_MESSAGE);
      default: {
        // Native plugin failures arrive as plain messages with no error.code —
        // recognize the common cases from the message text before giving up.
        const msg = (error?.message || '').toLowerCase();
        if (msg.includes('already in use') || msg.includes('already registered') || msg.includes('already linked')) {
          return friendly(alreadyInUseMessage);
        }
        if (msg.includes('invalid') && msg.includes('code')) {
          return friendly('That verification code isn\'t correct. Please double-check the 6-digit code from your text message and try again.');
        }
        if (msg.includes('expired')) {
          return friendly('Your verification code has expired. Please tap "Send Code" to get a new one.');
        }
        if (msg.includes('invalid') && msg.includes('phone')) {
          return friendly('That phone number doesn\'t look valid. Please enter a 10-digit US mobile number (e.g., 949-555-1234).');
        }
        if (msg.includes('too many') || msg.includes('too-many')) {
          return friendly('Too many attempts. Please wait a little while and try again.');
        }
        if (msg.includes('network')) {
          return friendly('Network connection issue. Please check your internet and try again.');
        }
        return friendly('Phone verification failed. Please try again, or contact us at contact@pedalxchange.com if this continues.');
      }
    }
  }

  // Clean up reCAPTCHA
  cleanup() {
    if (this.recaptchaVerifier) {
      try {
        this.recaptchaVerifier.clear();
      } catch (e) {
        console.warn('⚠️ Error clearing reCAPTCHA during cleanup:', e);
      }
      this.recaptchaVerifier = null;
    }
  }
}

export const firebasePhoneAuthService = new FirebasePhoneAuthService();
export default firebasePhoneAuthService; 